import { ThemeProvider } from '@emotion/react';
import moment from 'moment-timezone';
import React, { useEffect } from 'react';
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom';
import { SWRConfig } from 'swr';
import { PrimaryLayout } from './components/organisms/layout/PrimaryLayout';
import {
  AccessCode,
  UserProfile,
  ActivityLog,
  Coach,
  CoachGraphics,
  DrillGraphics,
  Drills,
  ExternalTools,
  FittingGraphics,
  HomeGraphics,
  HorizontalTiles,
  Login,
  NotFound,
  OtherGraphics,
  PermissionDenied,
  Play,
  PlayGraphics,
  Promotion,
  ServerHealth,
  ShopGraphics,
  TermsOfService,
  TextAssets,
  TourTrash,
  TryThenBuy,
  TryThenBuyFraud,
  VerticalTiles,
  VideoAssets,
  Product,
  Benefit,
  LaunchFeatures,
  LoyaltyAction,
  ProductTile,
  MoreForYou,
  ProductCatalog,
  Mainstays,
  HomeProduct,
  TheDaily,
  BackgroundImage,
  JustInClubhouse,
  PlayerCoachProfile,
  TourStories,
  CartnerConvos,
  Categories,
  MyGameProfile,
} from './components/pages';
import { ZONE } from './constants/common';
import { PAGES } from './constants/urls/page-urls';
import './index.css';
import './localization/i18n';
import { EcomProductContext, useEcomProductContext } from './repository/context/ecomProductContext';
import { SiderContext, useSiderContext } from './repository/context/siderContext';
import { UserContext, useUserContext } from './repository/context/userContext';
import { theme } from './theme';
import { isValidToken } from './utils/auth';
import { requestForToken, onMessageListener } from './firebase';
import { CountryContext, useCountryContext } from './repository/context/countryContext';
import { CountryFullContext, useCountryFullContext } from './repository/context/countryFullContext';
import { CountryOption, getCountryListRepository } from './repository/mytm/country/country';

moment.tz.setDefault(ZONE);

const PrivateRoute = ({ component: Component, ...rest }: any) => (
  <Route
    {...rest}
    render={props =>
      isValidToken() ? (
        <PrimaryLayout>
          <Component {...props} />
        </PrimaryLayout>
      ) : (
        <Redirect
          to={{
            pathname: PAGES.LOGIN,
          }}
        />
      )
    }
  />
);

const PublicRoute = ({ component: Component, ...rest }: any) => (
  <Route
    {...rest}
    render={props =>
      !isValidToken() ? (
        <Component {...props} />
      ) : (
        <Redirect
          to={{
            pathname: PAGES.HORIZONTAL_TILES,
          }}
        />
      )
    }
  />
);

const OriginalRoute = ({ component: Component, ...rest }: any) => (
  <Route {...rest} render={props => <Component {...props} />} />
);

export function App() {
  const userContext = useUserContext();
  const siderContext = useSiderContext();
  const ecomProductContext = useEcomProductContext();
  const countryContext = useCountryContext();
  const countryContextFull = useCountryFullContext();

  useEffect(() => {
    requestForToken(!!userContext.user);
  }, [userContext.user]);

  useEffect(() => {
    const getCountries = async () => {
      const countries: CountryOption[] = await getCountryListRepository();
      countryContextFull.updateCountryListFull(countries);

      // Get marketAccess from userContext
      const marketAccess = userContext.user?.marketAccess || null;

      // If null, default to USA
      const allowedCodes = marketAccess ? marketAccess.split(',').map((code: string) => code.trim()) : ['USA'];

      // Filter countries based on marketAccess
      const filteredCountries = countries.filter((country: { code: string }) => allowedCodes.includes(country.code));

      // If no filtered countries, default to USA if available, otherwise first country
      let defaultCountry: CountryOption[] = [];
      if (filteredCountries.length > 0) {
        defaultCountry = filteredCountries;
      } else {
        const usaCountry = countries.find((c: CountryOption) => c.code === 'USA');
        if (usaCountry) {
          defaultCountry = [usaCountry];
        } else if (countries.length > 0) {
          defaultCountry = [countries[0]];
        }
      }

      countryContext.updateCountryList(defaultCountry);
    };
    getCountries();
  }, [userContext.user?.marketAccess]);

  useEffect(() => {
    onMessageListener().then((payload: any) => {
      // console.log('onMessageListener', payload);
      const notificationTitle = payload.notification.title;
      const notificationOptions = {
        body: payload.notification.body,
      };

      const notification = new Notification(notificationTitle, notificationOptions);
      const { data } = payload;
      notification.onclick = event => {
        // console.log('on click');
        event.preventDefault();
        window.location.href = data.link;
      };
    });
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <SWRConfig value={{ provider: () => new Map() }}>
        <UserContext.Provider value={userContext}>
          <CountryFullContext.Provider value={countryContextFull}>
            <CountryContext.Provider value={countryContext}>
              <SiderContext.Provider value={siderContext}>
                <EcomProductContext.Provider value={ecomProductContext}>
                  <BrowserRouter>
                    <Switch>
                      <PublicRoute exact path={PAGES.LOGIN} component={Login} />
                      <OriginalRoute exact path={PAGES.TOS} component={TermsOfService} />
                      <PrivateRoute exact path={PAGES.HORIZONTAL_TILES} component={HorizontalTiles} />
                      <PrivateRoute exact path={PAGES.VERTICAL_TILES} component={VerticalTiles} />
                      <PrivateRoute exact path={PAGES.PLAY} component={Play} />
                      <PrivateRoute exact path={PAGES.DRILLS} component={Drills} />
                      <PrivateRoute exact path={PAGES.COACH} component={Coach} />
                      <PrivateRoute exact path={PAGES.TOUR_TRASH} component={TourTrash} />
                      <PrivateRoute exact path={PAGES.PROMOTION} component={Promotion} />
                      <PrivateRoute exact path={PAGES.PRODUCT} component={Product} />
                      <PrivateRoute exact path={PAGES.PERKS} component={Benefit} />
                      <PrivateRoute exact path={PAGES.TRY_THEN_BUY} component={TryThenBuy} />
                      <PrivateRoute exact path={PAGES.TRY_THEN_BUY_FRAUD} component={TryThenBuyFraud} />
                      <PrivateRoute exact path={PAGES.PRODUCT_TILE} component={ProductTile} />
                      <PrivateRoute exact path={PAGES.MORE_FOR_YOU} component={MoreForYou} />
                      <PrivateRoute exact path={PAGES.PRODUCT_CATALOG} component={ProductCatalog} />
                      <PrivateRoute exact path={PAGES.MAINSTAYS} component={Mainstays} />
                      <PrivateRoute exact path={PAGES.CATEGORIES} component={Categories} />
                      {/* ASSETS */}
                      <PrivateRoute exact path={PAGES.TEXT} component={TextAssets} />
                      <PrivateRoute exact path={PAGES.VIDEO} component={VideoAssets} />
                      {/* MISC */}
                      <PrivateRoute exact path={PAGES.USER_PROFILE} component={UserProfile} />
                      <PrivateRoute exact path={PAGES.LAUNCH_FEATURES} component={LaunchFeatures} />
                      <PrivateRoute exact path={PAGES.SHOW_HIDE_FEATURES} component={LaunchFeatures} />
                      <PrivateRoute exact path={PAGES.PAID_FEATURES} component={LaunchFeatures} />
                      <PrivateRoute exact path={PAGES.ACCESS_CODE} component={AccessCode} />
                      <PrivateRoute exact path={PAGES.LOYALTY_ACTION} component={LoyaltyAction} />
                      <PrivateRoute exact path={PAGES.MY_GAME_PROFILE} component={MyGameProfile} />
                      <PrivateRoute exact path={PAGES.ACTIVITY_LOG} component={ActivityLog} />
                      <PrivateRoute exact path={PAGES.EXTERNAL_TOOLS} component={ExternalTools} />
                      <PrivateRoute exact path={PAGES.SERVER_HEALTH} component={ServerHealth} />
                      {/* GRAPHICS */}
                      <PrivateRoute exact path={PAGES.GRAPHICS.HOME} component={HomeGraphics} />
                      <PrivateRoute exact path={PAGES.GRAPHICS.PLAY} component={PlayGraphics} />
                      <PrivateRoute exact path={PAGES.GRAPHICS.DRILLS} component={DrillGraphics} />
                      <PrivateRoute exact path={PAGES.GRAPHICS.COACH} component={CoachGraphics} />
                      <PrivateRoute exact path={PAGES.GRAPHICS.SHOP} component={ShopGraphics} />
                      <PrivateRoute exact path={PAGES.GRAPHICS.FITTING} component={FittingGraphics} />
                      <PrivateRoute exact path={PAGES.GRAPHICS.OTHER} component={OtherGraphics} />
                      {/* CUSTOM PAGE */}
                      <PrivateRoute exact path={PAGES.PERMISSION} component={PermissionDenied} />
                      {/* HOME */}
                      <PrivateRoute exact path={PAGES.HOME.PRODUCT} component={HomeProduct} />
                      <PrivateRoute exact path={PAGES.HOME.FOLDER.THE_DAILY} component={TheDaily} />
                      <PrivateRoute exact path={PAGES.HOME.CARTNER_CONVOS} component={CartnerConvos} />
                      {/* CLUB HOUSE */}
                      <PrivateRoute exact path={PAGES.CLUB_HOUSE.JUST_IN} component={JustInClubhouse} />
                      <PrivateRoute exact path={PAGES.CLUB_HOUSE.PLAYER} component={PlayerCoachProfile} />
                      <PrivateRoute exact path={PAGES.CLUB_HOUSE.TOUR_STORY} component={TourStories} />
                      {/* PLAY */}
                      <PrivateRoute exact path={PAGES.PLAY_BG_IMAGE} component={BackgroundImage} />
                      <OriginalRoute path="*" component={NotFound} />
                    </Switch>
                  </BrowserRouter>
                </EcomProductContext.Provider>
              </SiderContext.Provider>
            </CountryContext.Provider>
          </CountryFullContext.Provider>
        </UserContext.Provider>
      </SWRConfig>
    </ThemeProvider>
  );
}
